import { FormControlProps } from "@chakra-ui/form-control";
import { InputAddonProps, InputElementProps } from "@chakra-ui/input";
import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  InputLeftElement,
  InputRightAddon,
  InputRightElement,
  useMergeRefs,
} from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import React, { AriaRole, ChangeEventHandler, useMemo, useRef } from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import {
  InputContent,
  InputMessages,
  InputType,
  InputWrapper,
} from "shared/components/form/InputContent";
import { InputElementButton } from "shared/components/input/InputElementButton";
import { useActiveElement } from "shared/hooks/useActiveElement";
import { EyeIcon } from "shared/icons/EyeIcon";
import { EyeOffIcon } from "shared/icons/EyeOffIcon";
import { InputCalendarIcon } from "shared/icons/InputCalendarIcon";
import { InputXMarkIcon } from "shared/icons/InputXMarkIcon";
import { FormInputValue } from "shared/types/FormInputValue";
import { getFieldError } from "shared/utils/FormUtils";
import { useBoolean } from "usehooks-ts";

type TextInputType = "text" | "password" | "email";

export interface TextInputProps<T extends FormInputValue = string> {
  autoFocus?: boolean;
  autoComplete?: string;
  controlProps?: FormControlProps;
  error?: string;
  helpText?: string;
  inputType?: InputType;
  isDisabled?: boolean;
  isEditing?: boolean;
  leftAddon?: React.ReactNode;
  leftAddonProps?: InputAddonProps;
  leftElement?: React.ReactNode;
  leftElementProps?: InputElementProps;
  maxLength?: number;
  name?: string;
  onBlur?: () => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onChangeValue?: (value: T) => void;
  onClear?: () => void;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isReadOnly?: boolean;
  rightAddon?: React.ReactNode;
  rightAddonProps?: InputAddonProps;
  rightElement?: React.ReactNode;
  showMessages?: boolean;
  title?: string;
  type?: TextInputType;
  role?: AriaRole;
  value: T;
}

export type FormTextInputProps<T extends FormInputValue = string> = Omit<
  TextInputProps<T>,
  "name" | "value" | "error" | "onChangeValue" | "onChange"
> & {
  name: string;
  title?: string;
  shouldUnregister?: boolean;
};

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      autoFocus,
      autoComplete = "off",
      controlProps,
      error,
      helpText,
      inputType = "text",
      isDisabled,
      isEditing,
      leftAddon,
      leftAddonProps,
      maxLength,
      name,
      onBlur,
      onChange,
      onChangeValue,
      onClear,
      onFocus,
      onKeyDown,
      onMouseDown,
      placeholder,
      isReadOnly,
      rightAddon,
      rightAddonProps,
      leftElement: leftElementProp,
      leftElementProps,
      rightElement: rightElementProp,
      showMessages = true,
      title,
      type = "text",
      value,
      ...otherProps
    },
    ref,
  ) => {
    const { t } = useTranslation(["common"]);

    const inputRef = useRef<HTMLInputElement>();
    const mergedRefs = useMergeRefs(ref, inputRef);

    const activeElement = useActiveElement();
    const isFocused = activeElement === inputRef.current;

    const { value: showPassword, toggle: toggleShowPassword } = useBoolean();

    const leftElement = useMemo(() => {
      if (leftElementProp !== undefined) {
        return leftElementProp;
      }
      if (inputType === "date") {
        return (
          <InputCalendarIcon color="gray.400" width="24px" height="24px" />
        );
      }
    }, [inputType, leftElementProp]);

    const rightElement = useMemo(() => {
      if (onClear && value && (isFocused || isEditing) && !isReadOnly) {
        return (
          <InputElementButton
            aria-label={t("common:clear")}
            isDisabled={isDisabled}
            icon={
              <InputXMarkIcon width="24px" height="24px" color="gray.400" />
            }
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
          />
        );
      }
      if (type === "password") {
        const IconComponent = showPassword ? EyeOffIcon : EyeIcon;
        return (
          <InputElementButton
            aria-label={
              showPassword
                ? t("common:textInput.hidePassword")
                : t("common:textInput.showPassword")
            }
            icon={<IconComponent width="24px" height="24px" color="gray.400" />}
            onClick={toggleShowPassword}
          />
        );
      }
      return rightElementProp;
    }, [
      isDisabled,
      isReadOnly,
      isEditing,
      isFocused,
      onClear,
      rightElementProp,
      showPassword,
      t,
      toggleShowPassword,
      type,
      value,
    ]);

    const addonProps = useMemo(
      (): InputAddonProps => ({
        px: "8px",
        py: "11px",
        height: "unset",
        borderWidth: 0,
        onMouseDown: (e) => {
          e.preventDefault();
          inputRef.current?.focus();
        },
      }),
      [],
    );

    // TODO ENG-8089: consistent spacing
    return (
      <FormControl
        isInvalid={!!error}
        isDisabled={isDisabled}
        data-indeterminate={!value || undefined}
        data-editing={dataAttr(isEditing)}
        aria-readonly={dataAttr(isReadOnly)}
        {...controlProps}
      >
        <InputWrapper>
          <InputGroup
            size="lg"
            data-disabled={dataAttr(isDisabled)}
            _disabled={{
              cursor: "not-allowed",
              userSelect: "none",
            }}
          >
            {leftAddon && (
              <InputLeftAddon
                {...addonProps}
                {...leftAddonProps}
                borderRightWidth={1}
                borderRightColor="gray.300"
              >
                {leftAddon}
              </InputLeftAddon>
            )}
            <InputContent inputType={inputType}>
              <FormLabel
                variant="floating"
                marginLeft={leftElement ? "40px" : undefined}
              >
                {title}
              </FormLabel>
              {leftElement && (
                <InputLeftElement w="40px" top="-1px" {...leftElementProps}>
                  {leftElement}
                </InputLeftElement>
              )}
              <Input
                autoComplete={autoComplete}
                autoCorrect="off"
                autoFocus={autoFocus}
                paddingTop="19px"
                paddingBottom="7px"
                paddingLeft={leftElement ? "40px" : "16px"}
                paddingRight={rightElement ? "40px" : "16px"}
                ref={mergedRefs}
                maxLength={maxLength}
                name={name}
                placeholder={isFocused ? placeholder : undefined}
                type={type === "password" && showPassword ? "text" : type}
                data-blank={dataAttr(!value)}
                data-input-type={inputType}
                variant="unstyled"
                size="none"
                isDisabled={isDisabled}
                readOnly={isReadOnly}
                value={value}
                onChange={(e) => {
                  onChange?.(e);
                  onChangeValue?.(e.currentTarget.value);
                }}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onMouseDown={onMouseDown}
                {...otherProps}
              />
            </InputContent>
            {rightAddon && (
              <InputRightAddon
                {...addonProps}
                {...rightAddonProps}
                borderLeftWidth={1}
                borderLeftColor="gray.300"
              >
                {rightAddon}
              </InputRightAddon>
            )}
            {rightElement && (
              <InputRightElement top="-1px">{rightElement}</InputRightElement>
            )}
          </InputGroup>
        </InputWrapper>
        {showMessages && <InputMessages helpText={helpText} error={error} />}
      </FormControl>
    );
  },
);

TextInput.displayName = "TextInput";

export const FormTextInput = React.forwardRef<
  HTMLInputElement,
  FormTextInputProps
>(({ name, isDisabled, shouldUnregister = true, ...otherProps }, fwRef) => {
  const prefixedName = usePrefixedFieldName(name);
  const {
    field: { value, onChange, ref: fieldRef, ...field },
    formState: { errors },
  } = useController<{
    text: string;
  }>({
    name: prefixedName as "text",
    shouldUnregister,
    defaultValue: "",
  });
  const mergedRef = useMergeRefs(fwRef, fieldRef);
  const isFormDisabled = useIsFormDisabled();
  return (
    <TextInput
      {...field}
      {...otherProps}
      ref={mergedRef}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      isDisabled={isDisabled || isFormDisabled}
      error={getFieldError(errors, prefixedName)?.message}
    />
  );
});
FormTextInput.displayName = "FormTextInput";

export default TextInput;
